import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { FilterExpanderWithListOfCheckboxes } from '../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../components/CustomExpander/CustomExpander';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { AccountsPaymentsSideBarFilterType } from './accounts-payments-side-bar-filter.type';
import { CrmAccountType } from '../../../../api/types/crm/accounts/crm-account-entity';
import { PaymentMethodsDb } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface AccountsPaymentsSideBarFilterProps {
  onSearch: (filters: AccountsPaymentsSideBarFilterType) => void;
}

const crmAccountTypeDisplay = (type: CrmAccountType) => {
  switch (type) {
    case CrmAccountType.Company:
      return 'חברה';
    case CrmAccountType.Association:
      return 'עמותה';
    case CrmAccountType.NonProfit:
      return 'מלכ״ר';
    case CrmAccountType.SelfEmployed:
      return 'עצמאי';
    case CrmAccountType.Government:
      return 'גוף ממשלתי';
    case CrmAccountType.Institution:
      return 'מוסד';
    case CrmAccountType.Individual:
      return 'פרטי';
    case CrmAccountType.Other:
      return 'אחר';
    default:
      return type;
  }
};

const paymentMethodHebrew = (method: PaymentMethodsDb) => {
  switch (method) {
    case PaymentMethodsDb.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethodsDb.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethodsDb.Check:
      return 'המחאה';
    case PaymentMethodsDb.Scholarship:
      return 'מלגה';
    case PaymentMethodsDb.Cash:
      return 'מזומן';
    case PaymentMethodsDb.Discount:
      return 'הנחה';
    default:
      return method;
  }
};

export const AccountsPaymentsSideBarFilter = observer(
  ({ onSearch }: AccountsPaymentsSideBarFilterProps) => {
    const [branchIds, setBranchIds] = useState<number[]>([]);
    const [crmAccountTypes, setCrmAccountTypes] = useState<CrmAccountType[]>(
      [],
    );
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsDb[]>(
      [],
    );
    const [minAmount, setMinAmount] = useState<number | null>(null);
    const [maxAmount, setMaxAmount] = useState<number | null>(null);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);

    const handleBranchToggle = (id: number) => {
      setBranchIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    };

    const handleSearch = () => {
      onSearch({
        branchIds,
        crmAccountTypes,
        paymentMethods,
        minAmount,
        maxAmount,
        fromDate,
        toDate,
      });
    };

    const handleClearAll = () => {
      setBranchIds([]);
      setCrmAccountTypes([]);
      setPaymentMethods([]);
      setMinAmount(null);
      setMaxAmount(null);
      setFromDate(null);
      setToDate(null);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div
            style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}
          >
            סינונים
          </div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAll}
          />
        </div>

        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={branchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="סוג חשבון"
            items={
              Object.values(CrmAccountType).filter(
                (v) => typeof v === 'string',
              ) as CrmAccountType[]
            }
            selectedItems={crmAccountTypes}
            onItemToggle={(type: CrmAccountType) => {
              setCrmAccountTypes((prev) =>
                prev.includes(type)
                  ? prev.filter((t) => t !== type)
                  : [...prev, type],
              );
            }}
            getItemLabel={(type) => crmAccountTypeDisplay(type)}
            getItemKey={(type) => type}
            searchPlaceholder="חפש סוג..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="אמצעי תשלום"
            items={
              Object.values(PaymentMethodsDb).filter(
                (v) => typeof v === 'string',
              ) as PaymentMethodsDb[]
            }
            selectedItems={paymentMethods}
            onItemToggle={(method: PaymentMethodsDb) => {
              setPaymentMethods((prev) =>
                prev.includes(method)
                  ? prev.filter((m) => m !== method)
                  : [...prev, method],
              );
            }}
            getItemLabel={(m) => paymentMethodHebrew(m)}
            getItemKey={(m) => m}
            searchPlaceholder="חפש אמצעי..."
          />

          <CustomExpander>
            <div>טווח סכומים</div>
            <div style={{ padding: '8px', display: 'grid', gap: '10px' }}>
              <div className="p-field">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  מינימום
                </label>
                <InputNumber
                  value={minAmount}
                  onValueChange={(e) =>
                    setMinAmount((e.value as number) ?? null)
                  }
                  min={0}
                  maxFractionDigits={2}
                  style={{ width: '100%', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                />
              </div>
              <div className="p-field">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  מקסימום
                </label>
                <InputNumber
                  value={maxAmount}
                  onValueChange={(e) =>
                    setMaxAmount((e.value as number) ?? null)
                  }
                  min={0}
                  maxFractionDigits={2}
                  style={{ width: '100%', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl', textAlign: 'right' }}
                />
              </div>
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>טווח תאריכים</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>מתאריך</label>
                <Calendar
                  value={fromDate}
                  onChange={(e) => setFromDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{ height: '36px', width: '100%', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>עד תאריך</label>
                <Calendar
                  value={toDate}
                  onChange={(e) => setToDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{ height: '36px', width: '100%', direction: 'ltr' }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            onClick={handleSearch}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    );
  },
);
